import {ensurePreviousActiveDialogIsClosed} from '@github-ui/diff-lines/ensure-previous-active-dialog-is-closed'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {AlertIcon} from '@primer/octicons-react'
import {Button, IconButton} from '@primer/react'
import {useCallback, useRef, useState} from 'react'

import {AlertsSidePanel} from './AlertsSidePanel'
import {useAnnotations} from '../../stores/selectors'
import type {PageLimits} from '../../page-data/payloads/files'
import {usePullRequestAnalytics} from '../../hooks/use-pull-request-analytics'

export interface OpenAlertsPanelButtonProps {
  isFileTreeExpanded?: boolean
  pageLimits: PageLimits
  className?: string
}

/**
 *
 * Renders a button that opens the annotations side panel
 * Contains the total number of annotations plus the icon
 */
export function OpenAlertsPanelButton({isFileTreeExpanded, pageLimits, className}: OpenAlertsPanelButtonProps) {
  const [sidePanelIsOpen, setSidePanelIsOpen] = useState(false)
  const toggleSidePanelRef = useRef<HTMLButtonElement>(null)

  const handleClose = useCallback(() => setSidePanelIsOpen(false), [])

  const annotations = useAnnotations()
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  if (!annotations) {
    return null
  }

  const totalAnnotationsCount = annotations.length

  return totalAnnotationsCount > 0 ? (
    <div className="d-flex flex-items-center">
      <ErrorBoundary
        boundaryName="OpenAlertsPanelButton"
        fallback={
          <IconButton
            inactive
            aria-label="The alerts side panel cannot currently be opened."
            icon={AlertIcon}
            size="small"
            variant="invisible"
          />
        }
      >
        <Button
          ref={toggleSidePanelRef}
          className={className}
          aria-label="Open alerts side panel"
          count={totalAnnotationsCount}
          leadingVisual={AlertIcon}
          size="small"
          onClick={() => {
            sendPullRequestAnalyticsEvent('alerts_side_panel.open', 'ALERTS_SIDE_PANEL_BUTTON')
            ensurePreviousActiveDialogIsClosed()
            setSidePanelIsOpen(true)
          }}
        >
          {isFileTreeExpanded && <span className="d-none d-xl-block">Alerts</span>}
        </Button>
        <AlertsSidePanel
          annotations={annotations}
          isOpen={sidePanelIsOpen}
          pageLimits={pageLimits}
          returnFocusRef={toggleSidePanelRef}
          onClose={handleClose}
        />
      </ErrorBoundary>
    </div>
  ) : null
}
